// per docs/shape-ops-agreed-alerts.md §2.1 §2.10
// per specs/057-alert-query-builder/contracts/alert-builder.md
//
// The builder condition as the rule form edits it: every value a string while it is
// typed, turned back into the expression's own types on save. The vocabulary (sources,
// fields, functions, operators) is served by the backend, never copied here.
import type { useTranslate } from '@tolgee/react';
import type { AlertFieldKind, PublishedAlertCatalog } from '@simsm/shared-types';

type Translate = ReturnType<typeof useTranslate>['t'];

export type BuilderCatalog = PublishedAlertCatalog;

export interface BuilderFilterForm {
  field: string;
  op: string;
  value: string;
  /** The loaded value was a number. Used when the catalogue has not answered yet, so a
   *  save of an untouched rule does not turn its numbers into text. */
  numeric: boolean;
}

export interface BuilderForm {
  sources: string[];
  measureField: string;
  measureFn: string;
  groupBy: string[];
  context: string[];
  filters: BuilderFilterForm[];
  /** Empty means no window: the whole history. */
  windowAmount: string;
  windowUnit: 'hours' | 'days';
  /** Empty means the base relation's own time column. */
  windowField: string;
  comparisonOp: string;
  comparisonValue: string;
}

export const LIST_OPERATORS: ReadonlySet<string> = new Set([
  'in',
  'not_in',
  'contains_any',
  'contains_none',
]);

export function emptyBuilder(): BuilderForm {
  return {
    sources: ['stock'],
    measureField: 'movement_quantity',
    measureFn: 'sum',
    groupBy: [],
    context: [],
    filters: [],
    windowAmount: '',
    windowUnit: 'hours',
    windowField: '',
    comparisonOp: 'lt',
    comparisonValue: '',
  };
}

function record(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function strings(value: unknown): string[] {
  return Array.isArray(value) ? value.map((item) => String(item)) : [];
}

function scalarText(value: unknown): string {
  return value === null || value === undefined ? '' : String(value);
}

export function builderFromParams(params: Record<string, unknown>): BuilderForm {
  const measure = record(params['measure']);
  const comparison = record(params['comparison']);
  const window = params['window'] ? record(params['window']) : null;
  const filters = Array.isArray(params['filters']) ? params['filters'].map(record) : [];
  return {
    sources: strings(params['sources']),
    measureField: scalarText(measure['field']),
    measureFn: scalarText(measure['fn']),
    groupBy: strings(params['groupBy']),
    context: strings(params['context']),
    filters: filters.map((filter) => ({
      field: scalarText(filter['field']),
      op: scalarText(filter['op']) || 'eq',
      value: Array.isArray(filter['value'])
        ? filter['value'].map((item) => String(item)).join(', ')
        : scalarText(filter['value']),
      numeric: typeof filter['value'] === 'number',
    })),
    windowAmount: window ? scalarText(window['amount']) : '',
    windowUnit: window && window['unit'] === 'days' ? 'days' : 'hours',
    windowField: window ? scalarText(window['field']) : '',
    comparisonOp: scalarText(comparison['op']) || 'lt',
    comparisonValue: scalarText(comparison['value']),
  };
}

export function kindOf(
  catalog: BuilderCatalog | undefined,
  fieldId: string,
): AlertFieldKind | undefined {
  return catalog?.fields.find((field) => field.id === fieldId)?.kind;
}

export function builderToParams(
  form: BuilderForm,
  catalog: BuilderCatalog | undefined,
): Record<string, unknown> {
  const filters = form.filters
    .filter((filter) => filter.field)
    .map((filter) => {
      if (LIST_OPERATORS.has(filter.op)) {
        const items = filter.value
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean);
        return { field: filter.field, op: filter.op, value: items };
      }
      const raw = filter.value.trim();
      const kind = kindOf(catalog, filter.field);
      const numeric = kind ? kind === 'number' : filter.numeric;
      const asNumber = Number(raw);
      return {
        field: filter.field,
        op: filter.op,
        value: numeric && raw !== '' && Number.isFinite(asNumber) ? asNumber : raw,
      };
    });
  const amount = Number(form.windowAmount);
  const window =
    form.windowAmount.trim() !== '' && Number.isInteger(amount) && amount > 0
      ? { amount, unit: form.windowUnit, ...(form.windowField ? { field: form.windowField } : {}) }
      : null;
  return {
    sources: form.sources,
    measure: { field: form.measureField, fn: form.measureFn },
    groupBy: form.groupBy,
    context: form.context,
    filters,
    window,
    comparison: { op: form.comparisonOp, value: Number(form.comparisonValue) },
  };
}

export interface BuilderErrors {
  sources?: string;
  measure?: string;
  window?: string;
  filters?: string;
  comparison?: string;
}

// Translation keys, not messages: the form translates. Only what the form can know
// without the server; the compiler refuses the rest by name.
export function builderErrors(form: BuilderForm): BuilderErrors {
  const errors: BuilderErrors = {};
  if (form.sources.length === 0) errors.sources = 'alerts.builder.error.sources';
  if (!form.measureField || !form.measureFn) errors.measure = 'alerts.builder.error.measure';
  const amount = Number(form.windowAmount);
  if (form.windowAmount.trim() !== '' && !(Number.isInteger(amount) && amount > 0)) {
    errors.window = 'alerts.builder.error.window';
  }
  const incomplete = form.filters.some(
    (filter) => !filter.field || (!LIST_OPERATORS.has(filter.op) && filter.value.trim() === ''),
  );
  if (incomplete) errors.filters = 'alerts.builder.error.filter';
  const threshold = form.comparisonValue.trim();
  if (threshold === '' || !Number.isFinite(Number(threshold))) {
    errors.comparison = 'alerts.builder.error.comparison';
  }
  return errors;
}

// A builder condition in words, for the rule's page: one line per part, the labels the
// form uses.
export function describeBuilder(
  params: Record<string, unknown>,
  t: Translate,
): { key: string; label: string; value: string }[] {
  const form = builderFromParams(params);
  const field = (id: string): string => t(`alerts.query.${id}`, id);
  const op = (id: string): string => t(`alerts.builder.op.${id}`, id);
  const rows: { key: string; label: string; value: string }[] = [
    {
      key: 'sources',
      label: t('alerts.builder.sources'),
      value: form.sources.map((id) => t(`alerts.builder.source.${id}`, id)).join(', '),
    },
    {
      key: 'measure',
      label: t('alerts.builder.measure'),
      value: `${t(`alerts.builder.fn.${form.measureFn}`, form.measureFn)}: ${field(form.measureField)}`,
    },
    {
      key: 'groupBy',
      label: t('alerts.builder.group_by'),
      value: form.groupBy.map(field).join(', '),
    },
    {
      key: 'context',
      label: t('alerts.builder.context'),
      value: form.context.map(field).join(', '),
    },
    {
      key: 'filters',
      label: t('alerts.builder.filters'),
      value: form.filters
        .map((filter) => `${field(filter.field)} ${op(filter.op)} ${filter.value}`)
        .join('; '),
    },
    {
      key: 'window',
      label: t('alerts.builder.window'),
      value: form.windowAmount
        ? t('alerts.builder.window_value', {
            amount: form.windowAmount,
            unit: t(`alerts.builder.unit.${form.windowUnit}`),
          })
        : '',
    },
    {
      key: 'comparison',
      label: t('alerts.builder.comparison'),
      value: `${op(form.comparisonOp)} ${form.comparisonValue}`,
    },
  ];
  return rows.filter((row) => row.value.trim().length > 0);
}

// The enumerated values the application already names elsewhere keep those names; the
// builder's own two-label states (yes/no, active/inactive) and the request and
// submission states have keys of their own.
const VALUE_NAMESPACE: Record<string, string> = {
  movement_reason: 'stock_reporting.reason',
  registrar_kind: 'registrars.kind',
  site_type: 'sites.tip',
  geography_level: 'geography.kind',
};

export function valueLabelKey(fieldId: string, value: string): string {
  return `${VALUE_NAMESPACE[fieldId] ?? 'alerts.builder.value'}.${value}`;
}

// The compiler's refusals (docs/shape-ops-agreed-alerts.md §2.1). They belong to this
// form alone, so they are named here rather than in the application-wide error map.
const BUILDER_REFUSAL_CODES: ReadonlySet<string> = new Set([
  'INVALID_BUILDER_PARAMS',
  'UNKNOWN_SOURCE',
  'BUILDER_BASE_SOURCE',
  'SOURCE_NOT_JOINABLE',
  'UNKNOWN_FIELD',
  'FIELD_SOURCE_NOT_SELECTED',
  'FIELD_NOT_AVAILABLE',
  'INVALID_FUNCTION_FOR_KIND',
  'INVALID_OPERATOR_FOR_KIND',
  'INVALID_FILTER_VALUE',
  'INVALID_GROUP_BY',
  'INVALID_CONTEXT',
  'INVALID_WINDOW',
]);

export function builderRefusalKey(err: unknown): string | null {
  const shaped = err as { code?: unknown; response?: { data?: { code?: unknown } } } | null;
  const code = shaped?.code ?? shaped?.response?.data?.code;
  return typeof code === 'string' && BUILDER_REFUSAL_CODES.has(code)
    ? `alerts.builder.refusal.${code}`
    : null;
}
